import { FC, RefObject, Suspense, useMemo, useRef } from 'react';

import { RequirePermission } from '@gasbi/shared/common/providers/PermissionProvider';
import { useGetMeteringPointPermissionName } from '@gasbi/shared/common/providers/PermissionProvider';
import { usePageContext } from '@gasbi/shared/web/providers/PageProvider';
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom';

import { MeteringPointAct } from '@/features/meteringPointActs/types';
import Pages from '@/pages';
import { useShowEditButton } from '@/pages/MeteringPointActDetails/hooks/useShowEditButton';
import {
  CommercialMeteringPointActFormStep,
  MeteringPointActFormStep,
} from '@/pages/MeteringPointActForm';
import { CustomPortal } from '@/pages/MeteringPointActForm/views/Devices/components/CustomPortal';

import Button from '@mui/joy/Button';

import { IsModificationAllowedByEntity } from '@components/IsModificationAllowed';
import NavLinkTabs, { NavTabLink } from '@components/NavLinkTabs';
import { UserPermissions } from '@components/Permissions/userPermission';

export const ActFormView: Record<
  | keyof typeof MeteringPointActFormStep
  | keyof typeof CommercialMeteringPointActFormStep,
  string
> = {
  Information: '',
  Devices: 'devices',
  Equipments: 'equipments',
  Seals: 'seals',
  PipelineSection: 'pipelineSection',
};

type DetailsNavigationProps = {
  meteringPointAct?: MeteringPointAct;
  isCommercialMeteringPoint?: boolean;
};

export type MeteringPointActDetailsOutletContext = {
  outletRef: RefObject<HTMLDivElement>;
  meteringPointAct?: MeteringPointAct;
  isLoading?: boolean;
};

export const useMeteringPointActDetailsOutletContext = () =>
  useOutletContext<MeteringPointActDetailsOutletContext>();

export const MeteringPointActDetailsNavigation: FC<DetailsNavigationProps> = ({
  meteringPointAct,
  isCommercialMeteringPoint,
}) => {
  const navigate = useNavigate();
  const { loading } = usePageContext();
  const outletRef = useRef(null);

  const links: NavTabLink[] = useMemo(
    () => [
      {
        title: 'Загальна інформація',
        path: ActFormView.Information,
      },
      {
        title: 'Прилади',
        path: ActFormView.Devices,
      },
      {
        title: 'Обладнання',
        path: ActFormView.Equipments,
      },
      ...(isCommercialMeteringPoint
        ? [
            {
              title: 'Ділянки газопроводу',
              path: ActFormView.PipelineSection,
            },
          ]
        : []),
      {
        title: 'Пломби',
        path: ActFormView.Seals,
      },
    ],
    [isCommercialMeteringPoint],
  );

  const showEditButton = useShowEditButton();

  const meteringPointActEditPermissionName = useGetMeteringPointPermissionName({
    commercialMeteringPointPermission:
      UserPermissions.CommercialConsumersMeteringPointDocumentWrite,
    meteringPointPermission:
      UserPermissions.GasDistributionStationMeteringPointActWrite,
  });

  return (
    <>
      <CustomPortal containerRef={outletRef}>
        {showEditButton && (
          <RequirePermission name={meteringPointActEditPermissionName}>
            <IsModificationAllowedByEntity entity={meteringPointAct}>
              <Button onClick={() => navigate(Pages.METERING_POINT_ACTS_FORM)}>
                Редагувати
              </Button>
            </IsModificationAllowedByEntity>
          </RequirePermission>
        )}
      </CustomPortal>
      <NavLinkTabs ref={outletRef} links={links} loading={loading} />
      <Suspense>
        <Outlet context={{ outletRef, meteringPointAct, isLoading: loading }} />
      </Suspense>
    </>
  );
};
